import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import projectService from '../../../services/projectService';
import projectDashboardService from '../../../services/projectDashboardService';

const ModuleAssignmentModal = ({ isOpen, onClose, lineInfo, slotIndex, onAssign }) => {
  const [step, setStep] = useState('project'); // 'project' or 'module'
  const [projects, setProjects] = useState([]);
  const [modules, setModules] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Load projects on mount
  useEffect(() => {
    if (isOpen) {
      loadProjects();
    }
  }, [isOpen]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setStep('project');
      setSelectedProject(null);
      setSelectedModule(null);
      setModules([]);
      setSearchTerm('');
    }
  }, [isOpen]);

  const loadProjects = async () => {
    setLoading(true);
    try {
      const { data, error } = await projectService?.getAllProjects();
      if (!error && data) {
        setProjects(data);
      }
    } catch (err) {
      console.error('Error loading projects:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadModules = async (projectId) => {
    setLoading(true);
    try {
      const modules = await projectDashboardService?.getProjectModules(projectId);
      setModules(modules || []);
    } catch (err) {
      console.error('Error loading modules:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleProjectSelect = async (project) => {
    setSelectedProject(project);
    await loadModules(project?.id);
    setStep('module');
  };

  const handleModuleSelect = (module) => {
    setSelectedModule(module);
  };

  const handleAssign = () => {
    if (selectedModule && selectedProject) {
      onAssign?.({
        lineId: lineInfo?.id,
        slotIndex,
        module: selectedModule,
        project: selectedProject
      });
      onClose();
    }
  };

  const handleBack = () => {
    if (step === 'module') {
      setStep('project');
      setSelectedModule(null);
      setModules([]);
    }
  };

  const filteredProjects = projects?.filter(p => 
    p?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    p?.project_no?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  const filteredModules = modules?.filter(m => 
    m?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    m?.sku?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl border border-border w-full max-w-3xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            {step === 'module' && (
              <button
                onClick={handleBack}
                className="p-1 hover:bg-muted rounded-lg transition-colors"
              >
                <Icon name="ArrowLeft" size={20} color="var(--color-foreground)" />
              </button>
            )}
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                {step === 'project' ? 'Select Project' : 'Select Module'}
              </h2>
              <p className="text-sm text-muted-foreground">
                {lineInfo?.name} - Position {slotIndex + 1}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={20} color="var(--color-foreground)" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="px-6 py-4 border-b border-border">
          <div className="relative">
            <Icon 
              name="Search" 
              size={18} 
              color="var(--color-muted-foreground)" 
              className="absolute left-3 top-1/2 -translate-y-1/2"
            />
            <input
              type="text"
              placeholder={step === 'project' ? 'Search projects...' : 'Search modules...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full pl-10 pr-4 py-2 bg-muted border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Icon name="Loader2" size={32} color="var(--color-primary)" className="animate-spin" />
            </div>
          ) : step === 'project' ? (
            /* Project Selection */
            (<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredProjects?.length > 0 ? (
                filteredProjects?.map(project => (
                  <div
                    key={project?.id}
                    onClick={() => handleProjectSelect(project)}
                    className="bg-muted rounded-lg p-4 border-2 border-border hover:border-primary cursor-pointer transition-all group"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Icon name="FolderKanban" size={24} color="var(--color-primary)" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-foreground mb-1 truncate group-hover:text-primary transition-colors">
                          {project?.name}
                        </h3>
                        {project?.project_no && (
                          <p className="text-xs text-muted-foreground mb-2">
                            {project?.project_no}
                          </p>
                        )}
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Icon name="User" size={12} />
                            <span>{project?.owner}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <div 
                              className="w-2 h-2 rounded-full"
                              style={{
                                backgroundColor: 
                                  project?.status === 'completed' ? 'var(--color-success)' :
                                  project?.status === 'in-progress' ? 'var(--color-warning)' :
                                  'var(--color-muted-foreground)'
                              }}
                            />
                            <span className="capitalize">{project?.status}</span>
                          </div>
                        </div>
                      </div>
                      <Icon 
                        name="ChevronRight" 
                        size={20} 
                        color="var(--color-muted-foreground)" 
                        className="group-hover:text-primary transition-colors"
                      />
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-2 text-center py-12">
                  <Icon name="FolderOpen" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
                  <p className="text-muted-foreground">No projects found</p>
                </div>
              )}
            </div>)
          ) : (
            /* Module Selection */
            (<div className="space-y-3">
              {selectedProject && (
                <div className="bg-muted rounded-lg p-3 mb-4 flex items-center gap-3">
                  <Icon name="FolderKanban" size={20} color="var(--color-primary)" />
                  <div>
                    <p className="text-sm font-semibold text-foreground">{selectedProject?.name}</p>
                    <p className="text-xs text-muted-foreground">{modules?.length} modules available</p>
                  </div>
                </div>
              )}
              {filteredModules?.length > 0 ? (
                filteredModules?.map(module => {
                  const isSelected = selectedModule?.id === module?.id;
                  const moduleProgress = (module?.completed / (module?.quantity || 1)) * 100;
                  
                  return (
                    <div
                      key={module?.id}
                      onClick={() => handleModuleSelect(module)}
                      className={`bg-muted rounded-lg p-4 border-2 cursor-pointer transition-all ${
                        isSelected 
                          ? 'border-primary bg-primary/5' :'border-border hover:border-primary'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-foreground mb-1">{module?.name}</h3>
                          {module?.sku && (
                            <p className="text-xs text-muted-foreground mb-1">SKU: {module?.sku}</p>
                          )}
                          {module?.description && (
                            <p className="text-sm text-muted-foreground line-clamp-2">{module?.description}</p>
                          )}
                        </div>
                        {isSelected && (
                          <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center flex-shrink-0 ml-3">
                            <Icon name="Check" size={16} color="white" />
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-3 gap-3 mb-3">
                        <div className="bg-background rounded p-2">
                          <p className="text-xs text-muted-foreground mb-1">Quantity</p>
                          <p className="text-sm font-semibold text-foreground">{module?.quantity}</p>
                        </div>
                        <div className="bg-background rounded p-2">
                          <p className="text-xs text-muted-foreground mb-1">Completed</p>
                          <p className="text-sm font-semibold text-success">{module?.completed}</p>
                        </div>
                        <div className="bg-background rounded p-2">
                          <p className="text-xs text-muted-foreground mb-1">Progress</p>
                          <p className="text-sm font-semibold text-foreground">{Math.round(moduleProgress)}%</p>
                        </div>
                      </div>

                      <div className="w-full h-2 bg-background rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-primary rounded-full transition-all duration-500"
                          style={{ width: `${moduleProgress}%` }}
                        />
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-12">
                  <Icon name="Package" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
                  <p className="text-muted-foreground">No modules found in this project</p>
                </div>
              )}
            </div>)
          )}
        </div>

        {/* Footer */}
        {step === 'module' && (
          <div className="px-6 py-4 border-t border-border flex items-center justify-between">
            <Button
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAssign}
              disabled={!selectedModule}
            >
              <Icon name="Check" size={16} />
              <span className="ml-2">Assign Module</span>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModuleAssignmentModal;